import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import withRouter from "components/Common/withRouter"
import {
  Container,
  Row,
  Col,
  Label,
  Input,
  Alert,
  Spinner,
} from "reactstrap"
import { useParams } from "react-router-dom"
import Select from "react-select"

import {
  createDocumentRequest,
  getDocument,
  updateDocument,
  deleteDocument,
  clearDocumentErrors,
  assignDocumentCompanies,
  getDocuments,
} from "store/documents/actions"
import { getCompaniesAll } from "store/actions"
import { getCsrfHeaders } from "helpers/api_helper"
import { createTimedAbortSignal, resourceUploadTimeoutMs } from "helpers/resourceUpload"

const PDF_MAX_MB = 15
const VIDEO_MAX_MB = 500
const IMAGE_MAX_MB = 25

const ALLOWED_VIDEO_EXT = new Set([".mp4", ".mov", ".webm", ".wmv"]);

const ALLOWED_IMAGE_EXT = new Set([".png", ".jpg", ".jpeg", ".webp", ".svg"]);
const ALLOWED_IMAGE_MIME = new Set(["image/png", "image/jpeg", "image/webp", "image/svg+xml"]);

const ALLOWED_DOC_EXT = new Set([".pdf", ".doc", ".docx", ".ppt", ".pptx", ".zip"]);

const ALLOWED_DOC_MIME = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/zip",
  "application/x-zip-compressed",
]);

const DOC_TYPES_HINT = ".pdf, .doc, .docx, .ppt, .pptx, .zip";
const IMAGE_TYPES_HINT = ".png, .jpg, .jpeg, .webp, .svg";

const getExt = (name = "") => (name ? name.toLowerCase().match(/\.[a-z0-9]+$/)?.[0] || "" : "");

const isAllowedVideoByExt = (name = "") => ALLOWED_VIDEO_EXT.has(getExt(name));

const isAllowedType = (mime = "", name = "") => {
  const ext = getExt(name);

  // Extension is authoritative — browsers often send application/octet-stream for zip/pptx
  if (ALLOWED_DOC_EXT.has(ext) || ALLOWED_VIDEO_EXT.has(ext) || ALLOWED_IMAGE_EXT.has(ext)) return true;

  if (ALLOWED_IMAGE_MIME.has(mime)) return true;

  if (ALLOWED_DOC_MIME.has(mime)) return true;

  if (mime.startsWith("video/") && isAllowedVideoByExt(name)) return true;

  return false;
};

const isVideoByName = (name = "") => isAllowedVideoByExt(name);

const API_BASE = process.env.REACT_APP_API_BASE_URL;
// const API_BASE = "http://localhost:5000/api";
// const API_BASE = "http://192.168.100.4:5000/api";

const DocumentCreateUpdate = (props) => {
  const params = useParams()
  const docId = params?.id

  document.title = `${docId ? "Update" : "Create"} Document - ColdBlock Technologies Inc.`

  const dispatch = useDispatch();
  const {
    creating,
    updating,
    deleting,
    getDocumentLoading,
    message,
    assigning,
    document: selectedDoc,
  } = useSelector((state) => state.Documents || {})
  const themeColor = useSelector((state) => state.theme.color)
  const { user: loggedUser } = useSelector((state) => state.Login || {})
  const canManageCompaniesAssign = [1, 2].includes(Number(loggedUser?.role))

  const { allCompaniesData } = useSelector((s) => s.Company)

  useEffect(() => {
    document.documentElement.style.setProperty("--scrollbar-color", themeColor);
  }, [themeColor])

  useEffect(() => {
    if (canManageCompaniesAssign) {
      dispatch(getCompaniesAll());
    }
  }, [dispatch, canManageCompaniesAssign])

  const uploadViaApi = async (file, extra = {}, access = "private") => {
    const fd = new FormData();
    fd.append("file", file);
    if (extra.docId) fd.append("docId", String(extra.docId));
    if (extra.name) fd.append("name", extra.name);
    fd.append("access", access || "private");

    const timed = createTimedAbortSignal(resourceUploadTimeoutMs(file?.size));
    let resp;
    try {
      resp = await fetch(`${API_BASE}/uploads/direct`, {
        method: "POST",
        credentials: "include",
        headers: getCsrfHeaders(),
        body: fd,
        signal: timed.signal,
      });
    } finally {
      timed.clear();
    }

    const out = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(out?.message || "Upload failed");
    if (!out?.publicUrl) throw new Error("Upload succeeded but no publicUrl returned");
    return { fileUrl: out.publicUrl, fileKey: out.key };
  };

  const [name, setName] = useState("")
  const [file, setFile] = useState(null)
  const [companyIds, setCompanyIds] = useState([])
  const [showOnResources, setShowOnResources] = useState(true)
  const hydratedDocId = useRef(null)
  const [localError, setLocalError] = useState("")
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState("")

  useEffect(() => {
    if (docId) dispatch(getDocument(docId));
  }, [docId, dispatch]);

  useEffect(() => {
    if (!docId || !selectedDoc || String(selectedDoc.id) !== String(docId)) return
    if (hydratedDocId.current === String(docId)) return
    hydratedDocId.current = String(docId)
    setName(selectedDoc.name || "");
    setCompanyIds(
      Array.isArray(selectedDoc.companyIds)
        ? selectedDoc.companyIds.map(String)
        : []
    );
    setShowOnResources(selectedDoc.showOnResources !== false);
    setFile(null);
  }, [docId, selectedDoc]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => {
      dispatch(clearDocumentErrors?.());
    }, 8000);
    return () => clearTimeout(t);
  }, [message, dispatch]);

  const validate = () => {
    if (!name.trim()) return "Document name is required";
    if (!companyIds.length) return "Assign at least one company";
    if (!docId && !file) return "File is required";

    if (file) {
      const { size, type, name: fname } = file;
      const sizeMB = size / (1024 * 1024);

      // disallow early if type not allowed
      if (!isAllowedType(type, fname)) {
        return `Only videos (.mp4, .mov, .webm, .wmv), images (${IMAGE_TYPES_HINT}), or documents (${DOC_TYPES_HINT}) are allowed`;
      }

      // PDF size
      if ((type === "application/pdf" || /\.pdf$/i.test(fname)) && sizeMB > PDF_MAX_MB) {
        return `PDF must be ≤ ${PDF_MAX_MB} MB`;
      }

      // Word size
      const isWord =
        type === "application/msword" ||
        type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        /\.(docx?)$/i.test(fname);
      if (isWord && sizeMB > PDF_MAX_MB) {
        return `Word document must be ≤ ${PDF_MAX_MB} MB`;
      }

      // Video size (only our 4 types)
      if (isVideoByName(fname) && sizeMB > VIDEO_MAX_MB) {
        return `Video must be ≤ ${VIDEO_MAX_MB} MB`;
      }

      if ((ALLOWED_IMAGE_EXT.has(getExt(fname)) || ALLOWED_IMAGE_MIME.has(type)) && sizeMB > IMAGE_MAX_MB) {
        return `Image must be ≤ ${IMAGE_MAX_MB} MB`;
      }
    }

    return "";
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError("");
    setLocalError("");
    setUploadError("");

    const basePayload = {
      name: name.trim(),
      companyIds,
      showOnResources,
    };
    try {
      let uploadMeta = {};
      if (file) {
        setUploading(true);
        const { fileUrl, fileKey } = await uploadViaApi(file, {
          docId,
          name,
        });
        uploadMeta = {
          fileUrl,
          fileKey,
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          size: file.size,
        };
      }

      const payload = { ...basePayload, ...uploadMeta };

      if (docId) {
        dispatch(updateDocument(docId, payload, props.router.navigate, getDocuments));
      } else {
        dispatch(createDocumentRequest(payload, props.router.navigate, getDocuments));
      }
    } catch (e) {
      console.error(e);
      setUploadError(e?.error || e?.message || "File upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = () => {
    if (!docId) return;
    dispatch(deleteDocument(props.router.navigate, getDocuments, docId));
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (f && !isAllowedType(f.type, f.name)) {
      setLocalError(`Only videos (.mp4, .mov, .webm, .wmv), images (${IMAGE_TYPES_HINT}), or documents (${DOC_TYPES_HINT}) are allowed`);
    } else {
      setLocalError("");
    }
  };

  const onCompaniesChange = (opts) => {
    const ids = (opts || []).map(o => o.value);
    setCompanyIds(ids);
    if (docId) dispatch(assignDocumentCompanies(docId, ids, getDocuments))
  };

  const companyOptions = useMemo(
    () =>
      (allCompaniesData || []).map((c) => ({
        value: String(c.id),
        label: c.isPublic
          ? "Public"
          : c.isDistributor
            ? "Distributors"
            : `${c.name}${c.domain ? ` (${c.domain})` : ""}`,
        company: c,
      })),
    [allCompaniesData]
  );

  const selectedCompanies = useMemo(
    () => companyOptions?.filter((o) => companyIds?.includes(o.value)),
    [companyOptions, companyIds]
  );

  const renderOption = (option, { context }) => {
    const c = option.company;
    const logoSrc =
      c?.logoUrl || (c?.domain ? `https://logo.clearbit.com/${c.domain}` : null);
    const size = context === 'value' ? 16 : 18;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {logoSrc && (
          <img
            src={logoSrc}
            alt=""
            onError={e => (e.currentTarget.style.display = 'none')}
            style={{ width: size, height: size, borderRadius: 4, objectFit: 'cover' }}
          />
        )}
        <span>{option.label}</span>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content white-bg">
        <div className=" mb-4 d-flex align-items-center justify-content-between">
          <div>
            <div className="h2 mb-0 heading">{docId ? "Update" : "Add"} Resource</div>
            <p className="text-muted mb-0 mt-2">
              {docId ? "Edit details or replace the file." : "Upload a document, image, or video and assign it to companies."}
            </p>
          </div>
        </div>

        {!!message && (
          <Alert color="danger" className="mb-4">
            {String(message)}
          </Alert>
        )}
        {!!localError && (
          <Alert color="warning" className="mb-4">
            {localError}
          </Alert>
        )}
        {!!uploadError && (
          <Alert color="danger" className="mb-4">
            {uploadError}
          </Alert>
        )}

        {!docId || !getDocumentLoading ? (
          <Container fluid>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label className="form-label">Resource name</Label>
                  <Input
                    type="text"
                    placeholder="Enter resource name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <Label className="form-label">
                    {docId ? "Replace file (optional)" : "File"}
                  </Label>
                  <Input
                    type="file"
                    accept=".mp4,.mov,.webm,.wmv,.pdf,.doc,.docx,.ppt,.pptx,.zip,.png,.jpg,.jpeg,.webp,.svg"
                    onChange={handleFile}
                  />
                  {file ? (
                    <div className="mt-2 small text-muted">
                      Selected: {file.name} — {file.type || "unknown"} — {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </div>
                  ) : docId && selectedDoc?.originalFilename ? (
                    <div className="mt-2 small text-muted">Current: {selectedDoc.originalFilename}</div>
                  ) : null}
                  <div className="form-text">
                    PDFs and Word up to {PDF_MAX_MB} MB; images up to {IMAGE_MAX_MB} MB; PowerPoint, ZIP, and videos up to {VIDEO_MAX_MB} MB.
                    Supported: {DOC_TYPES_HINT}; images: {IMAGE_TYPES_HINT}; videos: .mp4, .mov, .webm, .wmv.
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <div className="mb-3">
                  <Label className="form-label d-flex align-items-center gap-2">
                    Assign to companies
                    {assigning ? <Spinner size="sm" /> : null}
                  </Label>

                  <Select
                    options={companyOptions}
                    value={selectedCompanies}
                    onChange={onCompaniesChange}
                    isMulti
                    menuPortalTarget={document.body}
                    isDisabled={creating || updating || uploading || assigning || deleting}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 999999, pointerEvents: 'auto' }),
                      menu: base => ({ ...base, zIndex: 999999, pointerEvents: 'auto' }),
                      option: base => ({ ...base, cursor: 'pointer', pointerEvents: 'auto' }),
                    }}
                    formatOptionLabel={renderOption}
                    placeholder="Search companies…"
                  />

                  <div className="form-text">You can select multiple companies.</div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <div className="mb-3">
                  <button
                    type="button"
                    className="d-inline-flex align-items-center gap-2 border-0 bg-transparent p-0"
                    aria-pressed={showOnResources}
                    disabled={creating || updating || uploading || assigning || deleting}
                    onClick={() => setShowOnResources((on) => !on)}
                  >
                    <span
                      aria-hidden="true"
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 4,
                        border: "1px solid #adb5bd",
                        background: showOnResources ? "#06427a" : "#fff",
                        color: "#fff",
                        fontSize: 14,
                        lineHeight: 1,
                      }}
                    >
                      {showOnResources ? "✓" : ""}
                    </span>
                    <span className="text-body">Show on Resources page</span>
                  </button>
                </div>
              </Col>
            </Row>

            <div className="action-btn-container">
              <button
                className="add-new-user cancel"
                onClick={() => props.router.navigate("/resources")}
                disabled={creating || updating || uploading || assigning || deleting}
              >
                Cancel
              </button>

              {docId && (
                <button
                  className="add-new-user decline"
                  onClick={onDelete}
                  disabled={creating || updating || uploading || assigning || deleting}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              )}

              <button
                className="add-new-user"
                onClick={onSubmit}
                disabled={creating || updating || uploading || assigning || deleting}
              >
                {creating || updating || uploading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {uploading
                      ? "Uploading..."
                      : docId
                        ? "Updating..."
                        : "Creating..."}
                  </>
                ) : docId ? (
                  "Update"
                ) : (
                  "Create resource"
                )}
              </button>
            </div>
          </Container>
        ) : (
          <div
            className="loading-container"
            style={{
              width: "100%",
              height: "60dvh",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Spinner />
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

DocumentCreateUpdate.propTypes = { router: PropTypes.object };
export default withRouter(DocumentCreateUpdate);
